import { useEffect, useRef, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const steps = [
  {
    number: '01',
    icon: 'ri-chat-3-line',
    title: 'Konzultácia zadarmo',
    text: 'Zavoláme si alebo sa stretneme. Chceme pochopiť vaše podnikanie, zákazníkov a ciele — nie len technické parametre. Trvá 30 minút.',
    duration: 'Deň 1',
  },
  {
    number: '02',
    icon: 'ri-palette-line',
    title: 'Dizajn a obsah',
    text: 'Navrhujeme vizuál, ktorý odráža vašu značku. Pomôžeme aj s textami — jednoducho, zrozumiteľne, predajne. Vy schvaľujete.',
    duration: 'Deň 2–5',
  },
  {
    number: '03',
    icon: 'ri-code-s-slash-line',
    title: 'Vývoj a testovanie',
    text: 'Kódujeme rýchlo a starostlivo. Pred spustením testujeme na rôznych zariadeniach a prehliadačoch. Žiadne prekvapenia.',
    duration: 'Deň 6–10',
  },
  {
    number: '04',
    icon: 'ri-rocket-line',
    title: 'Spustenie a podpora',
    text: 'Web ide live. Ste s nami aj po odovzdaní — hosting, bezpečnosť a úpravy podľa vašich potrieb. Nie sme tu len do spustenia.',
    duration: 'Deň 11–12',
  },
];

interface TWProcessProps {
  onCtaClick: () => void;
}

export default function TWProcess({ onCtaClick }: TWProcessProps) {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          <div
            className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-route-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-sm font-semibold">Ako to prebehne</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 mb-4 leading-tight">
              Od konzultácie po spustenie za{' '}
              <span className="text-orange-500">12 dní</span>
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed">
              Jasný proces, žiadne prekvapenia. Vždy viete, kde sa projekt nachádza a čo bude ďalej.
            </p>
          </div>

          <div className="relative">
            {/* Connector line */}
            <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-orange-200 to-transparent" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step, i) => (
                <div
                  key={step.number}
                  className={`relative flex flex-col items-center text-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  {/* Step circle */}
                  <div className="relative mb-6">
                    <div className="w-24 h-24 rounded-full bg-orange-50 border-2 border-orange-100 flex flex-col items-center justify-center">
                      <span className="text-orange-300 text-xs font-bold mb-1">{step.number}</span>
                      <i className={`${step.icon} text-orange-500 text-2xl`} />
                    </div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                      {step.duration}
                    </div>
                  </div>

                  <h3 className="text-zinc-900 font-bold text-base mb-3 leading-tight">{step.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`mt-16 text-center transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <button
              onClick={onCtaClick}
              className="inline-flex items-center gap-2.5 bg-orange-500 hover:bg-orange-400 text-white font-bold px-8 py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Začnime konzultáciou zadarmo
              <i className="ri-arrow-right-line" />
            </button>
            <p className="text-zinc-400 text-sm mt-3">Odpovieme do 2 hodín. Bez záväzkov.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
